import { useState, useEffect } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { ReactComponent as Trash } from "bootstrap-icons/icons/trash3.svg";
import { ReactComponent as PlusSquare } from "bootstrap-icons/icons/plus-square.svg";
import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";
import { ReactComponent as ArrowLeftSquare } from "bootstrap-icons/icons/arrow-left-square-fill.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import Spinner from "../spinner/Spinner";
import { Link, useNavigate, useLocation } from "react-router-dom";
import ApiServices from "../../api/ApiServices";
import AxiosApiCall from "../../api/AxiosApiCall";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import CustomStyles from "../../components/utils/CustomStyles";
import { handleError } from "../../components/utils/AuthenticationCheck";
import { useProductCategoryList } from "../../hooks/useAppData";
import { useQueryClient } from "@tanstack/react-query";

const ProductCategoryList = () => {
  const [subCategoryId, setSubCategoryId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [productCategory_id, setProductCategoryId] = useState("");
  const [filterText, setFilterText] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const axiosInst = AxiosApiCall();
  const queryClient = useQueryClient();

  useEffect(() => {
    const { id } = location.state || {};
    if (id) setSubCategoryId(id);
  }, [location.state]);

  useEffect(() => {
    const { categoryId: locCatId, subCategoryId: locSubCatId } = location.state || {};
    if (locCatId) setCategoryId(locCatId);
    if (locSubCatId) setSubCategoryId(locSubCatId);
  }, [location.state]);

  const { data: categoryResponse, isLoading: loading } = useProductCategoryList(
    subCategoryId ? { sub_category_id: subCategoryId } : {}
  );
  const productCategory = Array.isArray(categoryResponse?.data) ? categoryResponse.data : [];

  useEffect(() => {
    if (productCategory.length > 0) {
      setProductCategoryId(productCategory[0].product_category_id);
      setCategoryId(productCategory[0].category_id);
    }
  }, [productCategory.length]);



  // Handle delete category
  const handleDeleteCategory = async (productCategory_id) => {
    const formData = { product_category_id: productCategory_id };
    try {
      const response = await axiosInst.delete(ApiServices.removeProductsCategory, {
        data: formData,
        withCredentials: true,
      });

      const { status, message } = response.data;
      if (status === 200) {
        toast.success(message || "Category deleted successfully");
        queryClient.invalidateQueries({ queryKey: ["productCategoryList"] });
      } else {
        handleError({ response: { status, data: { message } } }, navigate);
      }
    } catch (error) {
      handleError(error, navigate);
    }
  };

  // Confirm delete
  const handleDeleteConfirmation = (productCategory_id) => {
    setConfirmationLoading(true);
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      setConfirmationLoading(false);
      if (result.isConfirmed) {
        handleDeleteCategory(productCategory_id);
      }
    });
  };

  // Filter data by name
  const handleFilter = (e) => {
    setFilterText(e.target.value);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  const columns = [
    {
      name: "Actions",
      width: "150px",
      cell: (row) => (
        <div className="flex justify-start">
          <Link
            to="/admin/product/category/edit"
            state={{ productCategory: row }}
            className="mx-4 my-2 group"
            style={{ marginLeft: "5px" }}
          >
            <PencilSquare className="w-5 h-5 text-dash4 mr-3" />
            <div className="absolute ml-4 top-full left-2/2 transform -translate-x-1/2 mb-1 hidden group-hover:block text-dash5 text-xs whitespace-nowrap">
              Edit
            </div>
          </Link>

          <Link
            className="mx-4 my-2 group"
            onClick={() => handleDeleteConfirmation(row.product_category_id)}
            style={{ marginLeft: "5px" }}
            data-toggle="tooltip"
            data-placement="bottom"
            title="Delete category"
            disabled={confirmationLoading}
          >
            <Trash className="w-5 h-5 text-dash3 mr-3" />
            <div className="absolute ml-4 top-full left-2/2 transform -translate-x-1/2 mb-1 hidden group-hover:block text-dash5 text-xs whitespace-nowrap">
              Delete
            </div>
          </Link>
        </div>
      ),
    },
    {
      name: "Name",
      selector: (row) => row.name.toUpperCase(),
      sortable: true,
      width: "300px",
    },
    {
      name: "Category Name",
      selector: (row) => row.category_name.toUpperCase(),
      sortable: true,
      width: "200px",
    },
    {
      name: "Sub Category Name",
      selector: (row) => row.sub_category_name.toUpperCase(),
      sortable: true,
      width: "200px",
    },
    {
      name: "Status",
      selector: (row) =>
        row.status ? row.status.toUpperCase() : "NOT AVAILABLE",
      sortable: true,
      cell: (row) => (
        <span className={row.status === "active" ? "text-dash" : "text-dash3"}>
          {row.status ? row.status.toUpperCase() : "NOT AVAILABLE"}
        </span>
      ),
      width: "150px",
    },
  ];

  const filteredItems = productCategory.filter((item) => {
    for (let key in item) {
      if (
        typeof item[key] === "string" &&
        item[key].toLowerCase().includes(filterText.toLowerCase())
      ) {
        return true;
      }
    }
    return false;
  });

  const data = filteredItems.map((category) => ({
    sub_category_name: category.sub_category_name || "NOT AVAILABLE",
    status: category.status || "NOT AVAILABLE",
    category_name: category.category_name
      ? category.category_name.toUpperCase()
      : "NOT AVAILABLE",
    sub_category_id: category.sub_category_id,
    category_id: category.category_id,
    name: category.name,
    product_category_id: category.product_category_id,
  }));

  // Sort data based on creation date
  const sortedData = [...data].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });

  return (
    <div>
      <MainLayouts>
        <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
          {/* <h1>Product Category List</h1> */}
          <section
            className="w-full  pr-[3%] pt-3 my-3"
            style={{ backgroundColor: "#F7F7F7" }}
          >
            <nav className="bg-gray-200 py-0 px-4 rounded-lg">
              <ol className="list-none p-0 inline-flex mb-2">
                <li className="flex items-center">
                  <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                    Category
                  </p>
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                    Sub Category
                  </p>
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                    Product Category
                  </p>
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <p className="text-orange1">List</p>
                </li>
              </ol>
            </nav>
          </section>
          <div className="pb-4 bg-white dark:bg-gray-900 flex flex-wrap justify-between">
            <div className="relative w-full sm:w-1/2 mb-4 sm:mb-0">
              <input
                type="search"
                className="block w-full px-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                placeholder="Search"
                value={filterText}
                onChange={handleFilter}
              />
            </div>
            <Link
              to="/admin/products/category/create"
              state={{ categoryId: subCategoryId }}
              className="m-1 w-full sm:w-auto text-dGrey3 bg-orange1 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center"
            >
              <span className=" flex justify-center sm:justify-start mx-2">
                <PlusSquare className="mr-2" />
                Create Product Category
              </span>
            </Link>
            <Link
              to="/admin/subcategory/list"
              state={{ categoryId }}
              // onClick={(e) => {
              //   // console.log("Current CategoryId:", categoryId);
              //   // Or get it from location.state directly
              //   // console.log("From location:", location.state?.categoryData);
              // }}
              className="m-1 w-full sm:w-auto text-dGrey3 bg-darkGreen3 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center"
            >
              <span className="flex justify-center sm:justify-start mx-2">
                <ArrowLeftSquare className="mr-2" />
                Back To Sub Category
              </span>
            </Link>
          </div>
          {loading ? (
            <Spinner loading={loading} />
          ) : (
            <DataTable
              columns={columns}
              data={sortedData}
              pagination
              striped
              highlightOnHover
              noDataComponent={<div>No Product Category found</div>}
              customStyles={CustomStyles}
            />
          )}
        </div>
      </MainLayouts>
    </div>
  );
};

export default ProductCategoryList;
